// Raw alarm list (WP 0.8): newest first, live `alarm` events prepended with a highlight,
// filter by site and by "received while paused" (contract 1.5), "Load older" with `before`, click a row for the
// detail drawer. A link can preset both (#/alarms?siteId=…&flag=received_paused, from a paused site's banner).

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api, errorText, type AlarmSummary } from '../api.ts';
import { AlarmDetailPanel } from '../components/AlarmDetailPanel.tsx';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon } from '../components/Icon.tsx';
import { Empty, ErrorBox, Flags, InlineError, Loading } from '../components/ui.tsx';
import { useLoad } from '../hooks.ts';
import { useStreamEvent } from '../stream.tsx';
import { siteAndLocal } from '../time.ts';

const PAGE = 100;
const SLOW_MS = 2000;
const FILTER_KEY = 'eyeqsoc.alarms.site';

function readFilter(): string {
  try {
    return localStorage.getItem(FILTER_KEY) ?? '';
  } catch {
    return '';
  }
}

function AlarmRow({ a, fresh, selected, onOpen }: { a: AlarmSummary; fresh: boolean; selected: boolean; onOpen: (id: string) => void }) {
  const t = siteAndLocal(a.receivedAt, a.siteTimeZone);
  const slow = a.latencyMs !== null && a.latencyMs > SLOW_MS;
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(a.id);
    }
  };
  return (
    <tr
      className={`click${fresh ? ' fresh' : ''}${selected ? ' sel' : ''}`}
      tabIndex={0}
      onClick={() => onOpen(a.id)}
      onKeyDown={onKey}
      aria-label={`Alarm ${a.displayNo ?? a.id}: ${a.analytic ?? 'unknown analytic'} at ${a.siteName ?? 'unknown site'}`}
    >
      <td className="mono nowrap" title={t.local}>
        {t.site}
        {!a.siteTimeZone && <span className="dim"> (no site zone)</span>}
      </td>
      <td className="ell">{a.siteName ?? <span className="warn-t">Unknown site</span>}</td>
      <td className="ell">{a.cameraName ?? <span className="warn-t">Unmapped</span>}</td>
      <td className="ell">
        {a.analytic ?? <span className="dim">—</span>}
        {a.burstCount > 1 && <span className="burst" title={`${a.burstCount} alarms collapsed into this burst`}>×{a.burstCount}</span>}
      </td>
      <td>
        <Flags flags={a.flags} />
      </td>
      <td className="mono num">{a.vendorConfidence === null ? '—' : a.vendorConfidence}</td>
      <td className={`mono num${slow ? ' slow' : ''}`} title="receivedAt → normalizedAt">
        {a.latencyMs === null ? '—' : a.latencyMs}
      </td>
      <td className="thumbcell">
        {a.snapshotUrl ? <AuthImg url={a.snapshotUrl} alt={`Snapshot for alarm ${a.displayNo ?? a.id}`} lazy className="thumb" /> : <span className="media-ph thumb">{a.hasClip ? 'Clip' : '—'}</span>}
      </td>
    </tr>
  );
}

export function AlarmsScreen({ siteId: linkedSite = null, flag: linkedFlag = null }: { siteId?: string | null; flag?: string | null } = {}) {
  const [siteId, setSiteId] = useState<string>(() => linkedSite ?? readFilter());
  const [flag, setFlag] = useState<string>(linkedFlag ?? '');
  const sites = useLoad((signal) => api.sites(undefined, signal), []);
  const list = useLoad((signal) => api.alarms({ limit: PAGE, siteId: siteId || undefined, flag: flag || undefined }, signal), [siteId, flag]);
  const rows = list.data;
  const setRows = list.setData;
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [older, setOlder] = useState<{ busy: boolean; error: unknown; done: boolean }>({ busy: false, error: null, done: false });
  const [paused, setPaused] = useState(false);
  const [buffered, setBuffered] = useState<AlarmSummary[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    try {
      localStorage.setItem(FILTER_KEY, siteId);
    } catch {
      /* ignore */
    }
    setOlder({ busy: false, error: null, done: false });
    setBuffered([]);
  }, [siteId, flag]);

  useEffect(() => {
    if (rows && rows.length < PAGE) setOlder((o) => ({ ...o, done: true }));
  }, [rows]);

  useEffect(() => {
    const t = timers.current;
    return () => t.forEach((x) => clearTimeout(x));
  }, []);

  const markFresh = useCallback((ids: string[]) => {
    setFresh((cur) => new Set([...cur, ...ids]));
    for (const id of ids) {
      const t = setTimeout(() => {
        setFresh((cur) => {
          const n = new Set(cur);
          n.delete(id);
          return n;
        });
        timers.current.delete(id);
      }, 2500);
      timers.current.set(id, t);
    }
  }, []);

  const prepend = useCallback(
    (items: AlarmSummary[]) => {
      setRows((cur) => {
        if (!cur) return cur;
        const seen = new Set(cur.map((r) => r.id));
        const add = items.filter((i) => !seen.has(i.id));
        return add.length ? [...add, ...cur] : cur;
      });
      markFresh(items.map((i) => i.id));
    },
    [setRows, markFresh],
  );

  useStreamEvent('alarm', (a) => {
    if (siteId && a.siteId !== siteId) return;
    if (flag && !a.flags.includes(flag)) return;
    if (paused) {
      setBuffered((b) => (b.some((x) => x.id === a.id) ? b : [a, ...b]));
      return;
    }
    prepend([a]);
  });

  function resume() {
    setPaused(false);
    if (buffered.length) prepend(buffered);
    setBuffered([]);
  }

  async function loadOlder() {
    const last = rows?.[rows.length - 1];
    if (!last) return;
    setOlder({ busy: true, error: null, done: false });
    try {
      const more = await api.alarms({ limit: PAGE, before: last.receivedAt, siteId: siteId || undefined, flag: flag || undefined });
      setRows((cur) => {
        const base = cur ?? [];
        const seen = new Set(base.map((r) => r.id));
        return [...base, ...more.filter((m) => !seen.has(m.id))];
      });
      setOlder({ busy: false, error: null, done: more.length < PAGE });
    } catch (error) {
      setOlder({ busy: false, error, done: false });
    }
  }

  const close = useCallback(() => setSelected(null), []);
  const siteList = (sites.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Raw alarm list</h2>
          <p>Every normalized alarm, newest first. New alarms appear live. Times are site-local; hover for yours.</p>
        </div>
        <span className="sp" />
        <label className="field inline">
          <span className="lbl">Site</span>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} aria-label="Filter by site">
            <option value="">All sites</option>
            {siteId && !siteList.some((s) => s.id === siteId) && <option value={siteId}>{siteId}</option>}
            {siteList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.companyName} · {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field inline">
          <span className="lbl">Show</span>
          <select value={flag} onChange={(e) => setFlag(e.target.value)} aria-label="Filter by flag">
            <option value="">All alarms</option>
            <option value="received_paused">Received while paused</option>
          </select>
        </label>
        {sites.error ? <span className="note warn-t" title={errorText(sites.error)}>Site list unavailable</span> : null}
        <button type="button" className={`btn${paused ? ' on' : ''}`} onClick={() => (paused ? resume() : setPaused(true))} aria-pressed={paused}>
          <Icon name={paused ? 'play' : 'pause'} />
          {paused ? `Resume${buffered.length ? ` (${buffered.length} new)` : ''}` : 'Pause live'}
        </button>
        <button type="button" className="btn" onClick={list.reload} disabled={list.loading}>
          <Icon name="sync" />
          Refresh
        </button>
      </div>

      <div className="card2 flush">
        {list.error && !rows ? (
          <ErrorBox error={list.error} onRetry={list.reload} title="Could not load alarms" />
        ) : !rows ? (
          <Loading label="Loading alarms…" />
        ) : rows.length === 0 ? (
          <Empty>
            No alarms yet{siteId ? ' for this site' : ''}. Send a test email to an ingest address (or run <span className="mono">npm run replay</span>) and it will show up here live.
          </Empty>
        ) : (
          <>
            {list.error ? <ErrorBox error={list.error} onRetry={list.reload} title="Refresh failed; showing earlier results" /> : null}
            <div className="tw">
              <table className="alarms-t">
                <thead>
                  <tr>
                    <th scope="col">Received (site)</th>
                    <th scope="col">Site</th>
                    <th scope="col">Camera</th>
                    <th scope="col">Analytic</th>
                    <th scope="col">Flags</th>
                    <th scope="col" className="num">Conf</th>
                    <th scope="col" className="num">Latency ms</th>
                    <th scope="col">Snapshot</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <AlarmRow key={a.id} a={a} fresh={fresh.has(a.id)} selected={selected === a.id} onOpen={setSelected} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tfoot">
              <span className="note">
                {rows.length} alarm{rows.length === 1 ? '' : 's'} shown
              </span>
              <span className="sp" />
              <InlineError error={older.error} />
              {older.done ? (
                <span className="note">No older alarms</span>
              ) : (
                <button type="button" className="btn" onClick={() => void loadOlder()} disabled={older.busy}>
                  {older.busy ? 'Loading…' : 'Load older'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {selected && <AlarmDetailPanel id={selected} onClose={close} />}
    </div>
  );
}
